import React from 'react';
import { Card, Space, Button, Typography, Tag, Row, Col } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, CopyOutlined } from '@ant-design/icons';
import { useScheduledDateRender, useUserTimestampRender } from '@src/hooks';
import { useStatusRender } from './useStatusRender';
import { useTypeRender } from './useTypeRender';
import { useOutcomeRender } from './useOutcomeRender';
import { MultilineTextField } from '@src/components/shared/fields';
import { FaWhatsapp } from 'react-icons/fa';

const { Text } = Typography;

interface FollowUpCardViewProps {
  dataSource: any[];
  onEditClick?: (record: any) => void;
  onDeleteClick?: (record: any) => void;
  onViewClick?: (record: any) => void;
  onRowClick?: (record: any) => void;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;
  customActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (record: any) => void;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    tooltip?: string;
    confirmMessage?: string;
  }>;
  rowKey?: string;
}

export const useFollowUpCardView = () => {
  const renderFollowUpDate = useScheduledDateRender();
  const getStatusConfig = useStatusRender();
  const getTypeConfig = useTypeRender();
  const getOutcomeConfig = useOutcomeRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  const FollowUpCardView: React.FC<FollowUpCardViewProps> = ({
    dataSource,
    onEditClick,
    onDeleteClick,
    onViewClick,
    onRowClick,
    showEdit = true,
    showDelete = true,
    showView = false,
    customActions = [],
    rowKey = 'id',
  }) => {
    // Helper function to render actions
    const renderActions = (record: any) => {
      const actions = [];

      if (showView) {
        actions.push(
          <Button
            key="view"
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onViewClick?.(record);
            }}
            style={{ color: '#1890ff' }}
          />
        );
      }

      if (showEdit) {
        actions.push(
          <Button
            key="edit"
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick?.(record);
            }}
            style={{ color: '#faad14' }}
          />
        );
      }

     
      if (showDelete) {
        actions.push(
          <Button
            key="delete"
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick?.(record);
            }}
            style={{ color: '#ff4d4f' }}
          />
        );
      }

      // Add custom actions
      customActions.forEach((action) => {
        actions.push(
          <Button
            key={action.key}
            type="text"
            size="small"
            icon={action.icon}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(record);
            }}
            style={{ 
              color: action.danger ? '#ff4d4f' : action.type === 'primary' ? '#1890ff' : '#666'
            }}
          />
        );
      });

      return actions;
    };

    return (
      <div className="p-0">
        {dataSource && dataSource.length > 0 ? dataSource.map((record, index) => {
          const patientName = record.patient_name || 'N/A';
          const patientPhone = record.patient_phone || '';
          const followUpDate = record.follow_up_date || '';
          const status = record.status || '';
          const type = record.type || '';
          const outcome = record.outcome || '';
          const attemptCount = record.attempt_count || 0;
          const remark = record.remark || '';
          const referenceId = record.reference_id || '';

          return (
            <Card
              key={record[rowKey] || index}
              size="small"
              className={`
                mb-4 mx-4 
                ${index === 0 ? 'mt-4' : 'mt-0'}
                rounded-xl shadow-sm border border-gray-200 
                ${onRowClick ? 'cursor-pointer' : 'cursor-default'}
                transition-all duration-200 ease-in-out bg-white
              `}
              bodyStyle={{ padding: '16px' }}
              onClick={() => onRowClick?.(record)}
            >
              {/* Top Section - ID, Type, Reference ID, Attempts */}
              <div className="flex justify-between items-center mb-3">
                {/* Left side - ID and Type */}
                <div className="flex items-center gap-2">
                  <Text className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    #{record[rowKey]}
                  </Text>
                  {type && getTypeConfig(type)}
                </div>
                
                {/* Right side - Ref ID and Attempts */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Ref ID</div>
                    <div className="text-xs font-medium text-blue-600 bg-blue-50 px-1 py-0.5 rounded inline-block">
                      #{referenceId}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Attempts</div>
                    <Tag color={attemptCount > 0 ? 'blue' : 'default'} style={{ fontSize: '11px', padding: '2px 8px' }}>
                      {attemptCount}
                    </Tag>
                  </div>
                </div>
              </div>

              {/* Patient Info Section with Status */}
              <div className="mb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="text-base font-bold text-black mb-1">
                      {patientName}
                    </div>
                    <div className="flex items-center gap-2">
                      <Text className="text-sm text-gray-600">
                        +{patientPhone || 'No phone'}
                      </Text>
                      {patientPhone && (
                        <FaWhatsapp
                          style={{ fontSize: '16px', color: '#25D366' }}
                          onClick={e => {
                            e.stopPropagation();
                            if (patientPhone) {
                              window.open(
                                `https://web.whatsapp.com/send/?phone=${patientPhone}`,
                                '_blank'
                              );
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                  {status && (
                    <div className="ml-4">
                      <Tag
                        icon={getStatusConfig(status).icon}
                        style={{
                          fontWeight: 500,
                          backgroundColor: getStatusConfig(status).bgColor,
                          color: getStatusConfig(status).textColor,
                          borderColor: getStatusConfig(status).borderColor,
                          border: `1px solid ${getStatusConfig(status).borderColor}`,
                          fontSize: '11px',
                          padding: '2px 8px',
                        }}
                      >
                        {getStatusConfig(status).text}
                      </Tag>
                    </div>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 mb-3"></div>

              {/* Middle Section - Follow Up Date and Outcome */}
              <Row gutter={[16, 8]} className="mb-3">
                <Col span={12}>
                  <div>
                    <Text type="secondary" className="text-xs">
                      Follow Up Date
                    </Text>
                    <div className="text-xs font-medium mt-0.5">
                      {renderFollowUpDate(followUpDate, status)}
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Text type="secondary" className="text-xs">
                      Outcome
                    </Text>
                    <div className="text-xs font-medium mt-0.5">
                      {outcome && getOutcomeConfig(outcome)}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Remark Section */}
              {remark && (
                <div className="mb-3">
                  <Text type="secondary" className="text-xs">
                    Remark
                  </Text>
                  <div className="text-xs font-medium mt-0.5">
                    <MultilineTextField text={remark} />
                  </div>
                </div>
              )}

              {/* Bottom Section - Creator and Actions */}
              <Row justify="space-between" align="middle">
                <Col>
                  <Space size="small">
                    {userTimestampRender(record)}
                  </Space>
                </Col>
                <Col>
                  <Space size="small">
                    {renderActions(record)}
                  </Space>
                </Col>
              </Row>
            </Card>
          );
        }) : (
          <div className="py-10 px-5 text-center text-gray-500 text-sm">
            No follow-ups found
          </div>
        )}
      </div>
    );
  };

  return { FollowUpCardView };
};
